'use client';

import { useEffect, useMemo, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduledTabPreview } from './_components/scheduled-tab-preview.component';

import { ScheduledModal } from '@/app/(main)/(pages)/_components/schedules/scheduled/scheduled-modal.component';
import { ScheduledSlotsGrid } from '@/app/(main)/(pages)/_components/schedules/scheduled/scheduled-slots-grid.component';
import { NearScheduleModal } from '@/app/(main)/(pages)/_components/schedules/near-schedule-modal/near-schedule-modal.component';
import { UnavailableScheduleModal } from '@/app/(main)/(pages)/_components/schedules/unavailable-schedule-modal/unavailable-schedule-modal.component';
import { dropPastScheduledSlots } from '@/app/(main)/(pages)/_components/schedules/_utils/drop-past-scheduled-slots';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import { useExpandedSlotsGrid } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-expanded-slots-grid.hook';
import {
  ScheduleType,
  SchedulesSearchSlot,
  SearchPartnerData,
} from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import {
  buildSlotCartItems,
  getCartItemsOfSlot,
  getOfferedProcedures,
  SLOT_ALREADY_IN_CART_MESSAGE,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_components/tabs/_utils/slot-cart-items.util';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import useDeviceType from '@/hooks/use-device-type.hook';
import { dateFormatter } from '@/utils/formatters/date';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { ADD_TO_CART_ERROR_MESSAGE, useAddToCart } from '@/hooks/use-add-to-cart.hook';
import { useCart } from '@/hooks/use-cart.hook';
import { isOnlineConsultationSearch } from '@/app/(main)/(pages)/busca/[slug]/_utils/is-online-consultation-search.util';
import { useCartAdditionConfirmation } from '@/hooks/use-cart-addition-confirmation.hook';
import { useRemoveFromCart } from '@/hooks/use-remove-from-cart.hook';
import { cartStore } from '@/store/cart/cart.store';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { ScheduleTabAnalytics } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';
import { trackSeeOtherSchedules } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';
import { CartRemovalScope } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { PatientOption } from '@/types/patient-selection.types';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { isScheduleNear } from '@/utils/schedule/is-schedule-near.util';
import { isSchedulePast } from '@/utils/schedule/is-schedule-past.util';

const CARD_SCHEDULED_DAYS = 3;

type SlotSelectionOrigin = 'preview' | 'inline' | 'grade' | 'modal';
const SLOTS_CLOCK_INTERVAL_MS = 60 * 1000;

const logger = getLogger('search.card-clinic-tab-scheduled');

interface SearchCardClinicTabScheduledProps {
  schedule: SearchPartnerData;
  tabAnalytics: ScheduleTabAnalytics;
}

export function SearchCardClinicTabScheduled({
  schedule,
  tabAnalytics,
}: SearchCardClinicTabScheduledProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nearSlot, setNearSlot] = useState<SchedulesSearchSlot | null>(null);
  const [shouldRestoreSlotsModal, setShouldRestoreSlotsModal] = useState(false);
  const [isUnavailableScheduleOpen, setIsUnavailableScheduleOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [busySlotIds, setBusySlotIds] = useState<ReadonlySet<string>>(() => new Set());
  const analytics = getAnalyticsPlugin();
  const scheduledSchedules = schedule.schedulesTypes[ScheduleType.SCHEDULED] ?? [];
  const scheduleIds = scheduledSchedules.map((item) => item.id);
  const partnerFacilityId = schedule.partner.facility.id;
  const slotFilters = useSlotFilters();
  const { requestPatientSelection } = usePatientSelection();
  const { addToCart } = useAddToCart({ onUnavailableSlot: () => setIsUnavailableScheduleOpen(true) });
  const { confirmCartAddition } = useCartAdditionConfirmation();
  const { removeItems } = useRemoveFromCart();
  const { items: cartItems } = useCart();
  const { toast } = useToast();

  const deviceType = useDeviceType();
  const {
    showsExpandedGrid, isCompact, slotsPerColumn, gridClassName, variant: experimentVariant,
  } = useExpandedSlotsGrid();

  const { isLoading, days: fetchedDays, hasMore, offset, goNext, goPrev, refresh } = useScheduleSlots({
    scheduleIds,
    limitDays: CARD_SCHEDULED_DAYS,
    filters: slotFilters,
  });

  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), SLOTS_CLOCK_INTERVAL_MS);

    return () => clearInterval(clock);
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    analytics.track(AnalyticsEnum.VIEW_ARRIVAL_ORDER_MODAL, {
      partner_id: schedule.partner.id,
      schedule_type: ScheduleType.SCHEDULED,
      variante: experimentVariant,
      device_type: deviceType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const days = dropPastScheduledSlots(fetchedDays, now);

  const { professional } = schedule;
  const entityName = professional?.id
    ? professionalDisplayName(professional)
    : partnerBranchDisplayName(schedule.partner);
  const entityAvatar = professional?.id ? professional.avatar : schedule.partner.avatar;
  const entityType = professional?.id ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.BRANCH;

  const markedSlotIds = useMemo(
    () => new Set(
      cartItems
        .filter((item) => item.partner.facility.id === partnerFacilityId)
        .map((item) => item.slot.id)
        .filter((id): id is string => Boolean(id)),
    ),
    [cartItems, partnerFacilityId],
  );

  const holdSlot = async (slotId: string, run: () => Promise<unknown>) => {
    setBusySlotIds((current) => new Set(current).add(slotId));

    try {
      await run();
    } finally {
      setBusySlotIds((current) => {
        const remaining = new Set(current);

        remaining.delete(slotId);

        return remaining;
      });
    }
  };

  const openModal = () => {
    trackSeeOtherSchedules(tabAnalytics, schedule);
    setIsModalOpen(true);
  };

  const handleSelectPatient = async (slot: SchedulesSearchSlot, patient: PatientOption) => {
    const unpricedProcedures = getOfferedProcedures(schedule).filter((procedure) => !procedure.price);

    if (unpricedProcedures.length > 0) {
      logger.error('Blocked a purchase without a procedure price', {
        cart: {
          event: 'unpriced_procedure',
          stage: 'price_guard',
          target: 'cart',
          partner_id: schedule.partner.id,
          partner_facility_id: partnerFacilityId,
          procedure_ids: unpricedProcedures.map((procedure) => procedure.id),
          slot_id: slot.id,
        },
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return;
    }

    await holdSlot(slot.id, () => addToCart(buildSlotCartItems(schedule, patient.id, slot.id)));
  };

  const askForPatient = (slot: SchedulesSearchSlot, restoreSlotsModal: boolean) => {
    const slotSchedule = scheduledSchedules.find((item) => item.id === slot.scheduleId);

    requestPatientSelection({
      ageRange: { minAge: slotSchedule?.minAge, maxAge: slotSchedule?.maxAge },
      onBeforeSelection: async () => {
        if (isSchedulePast(slot.start)) {
          setIsUnavailableScheduleOpen(true);

          return false;
        }

        const confirmed = await confirmCartAddition(
          {
            partnerId: schedule.partner.id,
            facility: schedule.partner.facility,
            isOnline: isOnlineConsultationSearch(schedule),
          },
          ScheduleType.SCHEDULED,
          { isStandalone: false },
        );

        if (!confirmed) {
          setIsModalOpen(restoreSlotsModal);

          return false;
        }

        const { cart } = cartStore.getState();

        if (getCartItemsOfSlot(cart?.items ?? [], partnerFacilityId, slot.id).length > 0) {
          logger.info('Dropped a purchase because the known cart already holds the slot', {
            cart: {
              event: 'slot_already_in_cart',
              stage: 'cart_guard',
              target: 'cart',
              partner_id: schedule.partner.id,
              partner_facility_id: partnerFacilityId,
              slot_id: slot.id,
            },
          });
          toast.warn(SLOT_ALREADY_IN_CART_MESSAGE);
          setIsModalOpen(restoreSlotsModal);

          return false;
        }

        if (isSchedulePast(slot.start)) {
          setIsUnavailableScheduleOpen(true);

          return false;
        }

        return true;
      },
      onSelectPatient: (patient) => handleSelectPatient(slot, patient),
    });
  };

  const handleSelectSlot = (slot: SchedulesSearchSlot, origin: SlotSelectionOrigin) => {
    if (busySlotIds.has(slot.id)) {
      return;
    }

    const itemsOfSlot = getCartItemsOfSlot(cartItems, partnerFacilityId, slot.id);

    if (itemsOfSlot.length > 0) {
      void holdSlot(slot.id, () => removeItems(itemsOfSlot, CartRemovalScope.SLOT));

      return;
    }

    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_SLOT, {
      partner_id: schedule.partner.id,
      professional_id: schedule.professional?.id,
      schedule_type: ScheduleType.SCHEDULED,
      data: slot.date,
      horario: dateFormatter(slot.start, 'HH:mm'),
      price: sumProcedurePrices(schedule.procedures),
      origem: origin,
      variante: experimentVariant,
      device_type: deviceType,
    });

    const wasSlotsModalOpen = isModalOpen;

    setShouldRestoreSlotsModal(wasSlotsModalOpen);
    setIsModalOpen(false);

    if (isSchedulePast(slot.start)) {
      setIsUnavailableScheduleOpen(true);
      return;
    }

    if (isScheduleNear(slot.start)) {
      setNearSlot(slot);
      return;
    }

    askForPatient(slot, wasSlotsModalOpen);
  };

  const handleNearScheduleConfirm = () => {
    const confirmedSlot = nearSlot;
    setNearSlot(null);

    if (!confirmedSlot) {
      return;
    }

    if (isSchedulePast(confirmedSlot.start)) {
      setIsUnavailableScheduleOpen(true);
      return;
    }

    analytics.track(AnalyticsEnum.ACCEPTED_NEAR_SCHEDULE_WARNING, {
      partner_id: schedule.partner.id,
      schedule_type: ScheduleType.SCHEDULED,
    });

    askForPatient(confirmedSlot, shouldRestoreSlotsModal);
  };

  const handleUnavailableScheduleClose = () => {
    setIsUnavailableScheduleOpen(false);
    setIsModalOpen(shouldRestoreSlotsModal);
    setNow(new Date());
    refresh();
  };

  const handleNearScheduleDismiss = () => {
    setNearSlot(null);
    setIsModalOpen(shouldRestoreSlotsModal);
  };

  return (
    <div data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_TAB_SCHEDULED}>
      {isLoading
        ? (
            <>
              {!showsExpandedGrid && <SlotsSkeleton variant="card" className="xl:hidden" />}
              <SlotsSkeleton
                variant="modal"
                columns={CARD_SCHEDULED_DAYS}
                slotsPerColumn={slotsPerColumn}
                className={gridClassName}
              />
            </>
          )
        : (
            <>
              {!showsExpandedGrid && (
                <ScheduledTabPreview
                  day={days[0]}
                  markedSlotIds={markedSlotIds}
                  busySlotIds={busySlotIds}
                  className="xl:hidden"
                  onSeeMore={openModal}
                  onSelectSlot={(slot) => handleSelectSlot(slot, 'preview')}
                />
              )}
              <ScheduledSlotsGrid
                days={days}
                markedSlotIds={markedSlotIds}
                busySlotIds={busySlotIds}
                hasMore={hasMore}
                offset={offset}
                className={gridClassName}
                slotsPerColumn={slotsPerColumn}
                compact={isCompact}
                onPrev={goPrev}
                onNext={goNext}
                onSeeMore={openModal}
                onSelectSlot={(slot) => handleSelectSlot(slot, showsExpandedGrid ? 'inline' : 'grade')}
              />
            </>
          )}

      <ScheduledModal
        scheduleIds={scheduleIds}
        entityName={entityName}
        entityAvatar={entityAvatar}
        entityType={entityType}
        now={now}
        markedSlotIds={markedSlotIds}
        busySlotIds={busySlotIds}
        open={isModalOpen}
        slotFilters={slotFilters}
        dataTestId={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_MODAL}
        closeDataTestId={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_MODAL_CLOSE}
        toggleExpandDataTestId={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_TOGGLE_EXPAND}
        onOpenChange={setIsModalOpen}
        onSelectSlot={(slot) => handleSelectSlot(slot, 'modal')}
      />

      <NearScheduleModal
        open={nearSlot !== null}
        partnerId={schedule.partner.id}
        scheduleType={ScheduleType.SCHEDULED}
        onConfirm={handleNearScheduleConfirm}
        onDismiss={handleNearScheduleDismiss}
      />

      <UnavailableScheduleModal
        open={isUnavailableScheduleOpen}
        onClose={handleUnavailableScheduleClose}
      />
    </div>
  );
}
